import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SidebarAdmin from "./sidebar-admin";
import { API_URL } from "../config";

// Interfaces
interface DocumentItem {
  id: number;
  document_type: string;
  original_name: string;
  file_path: string;
  mime_type: string;
  size: number;
  url: string;
  uploaded_by_type?: "admin" | "client";
  created_at: string;
}

interface PaymentRecord {
  id: number;
  amount: number;
  currency: string;
  payment_method: string;
  transaction_id?: string;
  status: "pending" | "completed" | "failed" | "refunded";
  description: string;
  paid_by?: string;
  created_at: string;
}

interface NoteItem {
  id: number;
  content: string;
  created_by_name: string;
  created_by_type: "admin" | "system";
  created_at: string;
}

interface ActivityLog {
  id: number;
  action_type: string;
  action: string;
  description: string;
  performed_by_type: "admin" | "client" | "system";
  performed_by_name: string;
  metadata?: any;
  created_at: string;
}

interface Communication {
  id: number;
  subject: string;
  message: string;
  sender_type: "admin" | "client";
  sender_name: string;
  attachments?: any[];
  created_at: string;
}

interface VisaPack {
  id: number;
  name_en?: string;
  name_ar?: string;
  price?: number;
}

interface VisaApplicationDetail {
  id: number;
  reference_number: string;
  firstname: string;
  lastname: string;
  email: string;
  contact_no: string;
  nationality: string;
  gender: string;
  passport_no: string;
  purpose_of_visit: string;
  current_address: string;
  city: string;
  state?: string;
  zipcode?: string;
  status: "pending" | "under_review" | "documents_requested" | "approved" | "rejected" | string;
  admin_notes?: string;
  visa_number?: string;
  visa_issue_date?: string;
  visa_expiry_date?: string;
  appointment_date?: string;
  appointment_location?: string;
  created_at: string;
  updated_at: string;
  visa_pack?: VisaPack;
  documents: DocumentItem[];
  payments: PaymentRecord[];
  notes: NoteItem[];
  activities: ActivityLog[];
  communications: Communication[];
}

const VisaApplicationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [application, setApplication] = useState<VisaApplicationDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Status
  const [status, setStatus] = useState<string>("pending");
  const [adminNotes, setAdminNotes] = useState<string>("");

  // Document upload
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadDocumentType, setUploadDocumentType] = useState<string>("additional_document");

  // Modals
  const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState<boolean>(false);
  const [showVisaModal, setShowVisaModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // Form states
  const [newNote, setNewNote] = useState<string>("");
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentCurrency, setPaymentCurrency] = useState<string>("OMR");
  const [paymentMethod, setPaymentMethod] = useState<string>("cash");
  const [paymentTransactionId, setPaymentTransactionId] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("completed");
  const [paymentDescription, setPaymentDescription] = useState<string>("");
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailMessage, setEmailMessage] = useState<string>("");
  const [emailTemplate, setEmailTemplate] = useState<string>("custom");
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [appointmentDate, setAppointmentDate] = useState<string>("");
  const [appointmentLocation, setAppointmentLocation] = useState<string>("");
  const [appointmentNotes, setAppointmentNotes] = useState<string>("");
  const [visaNumber, setVisaNumber] = useState<string>("");
  const [visaIssueDate, setVisaIssueDate] = useState<string>("");
  const [visaExpiryDate, setVisaExpiryDate] = useState<string>("");

  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);

  const [paymentCurrencySearch, setPaymentCurrencySearch] = useState<string>("");
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState<boolean>(false);

  const currencies = [
  { code: "AFN", name: "Afghan Afghani" },
  { code: "ALL", name: "Albanian Lek" },
  { code: "DZD", name: "Algerian Dinar" },
  { code: "AOA", name: "Angolan Kwanza" },
  { code: "ARS", name: "Argentine Peso" },
  { code: "AMD", name: "Armenian Dram" },
  { code: "AWG", name: "Aruban Florin" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "AZN", name: "Azerbaijani Manat" },
  { code: "BSD", name: "Bahamian Dollar" },
  { code: "BHD", name: "Bahraini Dinar" },
  { code: "BDT", name: "Bangladeshi Taka" },
  { code: "BBD", name: "Barbadian Dollar" },
  { code: "BYN", name: "Belarusian Ruble" },
  { code: "BZD", name: "Belize Dollar" },
  { code: "BMD", name: "Bermudian Dollar" },
  { code: "BTN", name: "Bhutanese Ngultrum" },
  { code: "BOB", name: "Bolivian Boliviano" },
  { code: "BAM", name: "Bosnia-Herzegovina Convertible Mark" },
  { code: "BWP", name: "Botswana Pula" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "BND", name: "Brunei Dollar" },
  { code: "BGN", name: "Bulgarian Lev" },
  { code: "BIF", name: "Burundian Franc" },
  { code: "KHR", name: "Cambodian Riel" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "CVE", name: "Cape Verdean Escudo" },
  { code: "KYD", name: "Cayman Islands Dollar" },
  { code: "XAF", name: "Central African CFA Franc" },
  { code: "CLP", name: "Chilean Peso" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "COP", name: "Colombian Peso" },
  { code: "KMF", name: "Comorian Franc" },
  { code: "CDF", name: "Congolese Franc" },
  { code: "CRC", name: "Costa Rican Colón" },
  { code: "HRK", name: "Croatian Kuna" },
  { code: "CUP", name: "Cuban Peso" },
  { code: "CZK", name: "Czech Koruna" },
  { code: "DKK", name: "Danish Krone" },
  { code: "DJF", name: "Djiboutian Franc" },
  { code: "DOP", name: "Dominican Peso" },
  { code: "XCD", name: "East Caribbean Dollar" },
  { code: "EGP", name: "Egyptian Pound" },
  { code: "ERN", name: "Eritrean Nakfa" },
  { code: "ETB", name: "Ethiopian Birr" },
  { code: "EUR", name: "Euro" },
  { code: "FKP", name: "Falkland Islands Pound" },
  { code: "FJD", name: "Fijian Dollar" },
  { code: "GMD", name: "Gambian Dalasi" },
  { code: "GEL", name: "Georgian Lari" },
  { code: "GHS", name: "Ghanaian Cedi" },
  { code: "GIP", name: "Gibraltar Pound" },
  { code: "GTQ", name: "Guatemalan Quetzal" },
  { code: "GNF", name: "Guinean Franc" },
  { code: "GYD", name: "Guyanese Dollar" },
  { code: "HTG", name: "Haitian Gourde" },
  { code: "HNL", name: "Honduran Lempira" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "HUF", name: "Hungarian Forint" },
  { code: "ISK", name: "Icelandic Króna" },
  { code: "INR", name: "Indian Rupee" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "IRR", name: "Iranian Rial" },
  { code: "IQD", name: "Iraqi Dinar" },
  { code: "ILS", name: "Israeli New Shekel" },
  { code: "JMD", name: "Jamaican Dollar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "JOD", name: "Jordanian Dinar" },
  { code: "KZT", name: "Kazakhstani Tenge" },
  { code: "KES", name: "Kenyan Shilling" },
  { code: "KWD", name: "Kuwaiti Dinar" },
  { code: "KGS", name: "Kyrgyzstani Som" },
  { code: "LAK", name: "Lao Kip" },
  { code: "LBP", name: "Lebanese Pound" },
  { code: "LSL", name: "Lesotho Loti" },
  { code: "LRD", name: "Liberian Dollar" },
  { code: "LYD", name: "Libyan Dinar" },
  { code: "MOP", name: "Macanese Pataca" },
  { code: "MGA", name: "Malagasy Ariary" },
  { code: "MWK", name: "Malawian Kwacha" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "MVR", name: "Maldivian Rufiyaa" },
  { code: "MRO", name: "Mauritanian Ouguiya" },
  { code: "MUR", name: "Mauritian Rupee" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "MDL", name: "Moldovan Leu" },
  { code: "MNT", name: "Mongolian Tögrög" },
  { code: "MAD", name: "Moroccan Dirham" },
  { code: "MZN", name: "Mozambican Metical" },
  { code: "MMK", name: "Myanmar Kyat" },
  { code: "NAD", name: "Namibian Dollar" },
  { code: "NPR", name: "Nepalese Rupee" },
  { code: "ANG", name: "Netherlands Antillean Guilder" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "NIO", name: "Nicaraguan Córdoba" },
  { code: "NGN", name: "Nigerian Naira" },
  { code: "NOK", name: "Norwegian Krone" },
  { code: "OMR", name: "Omani Rial" },
  { code: "PKR", name: "Pakistani Rupee" },
  { code: "PAB", name: "Panamanian Balboa" },
  { code: "PGK", name: "Papua New Guinean Kina" },
  { code: "PYG", name: "Paraguayan Guaraní" },
  { code: "PEN", name: "Peruvian Sol" },
  { code: "PHP", name: "Philippine Peso" },
  { code: "PLN", name: "Polish Złoty" },
  { code: "QAR", name: "Qatari Riyal" },
  { code: "RON", name: "Romanian Leu" },
  { code: "RUB", name: "Russian Ruble" },
  { code: "RWF", name: "Rwandan Franc" },
  { code: "SHP", name: "Saint Helena Pound" },
  { code: "SVC", name: "Salvadoran Colón" },
  { code: "WST", name: "Samoan Tālā" },
  { code: "STD", name: "São Tomé and Príncipe Dobra" },
  { code: "SAR", name: "Saudi Riyal" },
  { code: "RSD", name: "Serbian Dinar" },
  { code: "SCR", name: "Seychellois Rupee" },
  { code: "SLL", name: "Sierra Leonean Leone" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "SBD", name: "Solomon Islands Dollar" },
  { code: "SOS", name: "Somali Shilling" },
  { code: "ZAR", name: "South African Rand" },
  { code: "KRW", name: "South Korean Won" },
  { code: "SSP", name: "South Sudanese Pound" },
  { code: "LKR", name: "Sri Lankan Rupee" },
  { code: "SDG", name: "Sudanese Pound" },
  { code: "SRD", name: "Surinamese Dollar" },
  { code: "SZL", name: "Swazi Lilangeni" },
  { code: "SEK", name: "Swedish Krona" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "SYP", name: "Syrian Pound" },
  { code: "TWD", name: "New Taiwan Dollar" },
  { code: "TJS", name: "Tajikistani Somoni" },
  { code: "TZS", name: "Tanzanian Shilling" },
  { code: "THB", name: "Thai Baht" },
  { code: "TOP", name: "Tongan Paʻanga" },
  { code: "TTD", name: "Trinidad and Tobago Dollar" },
  { code: "TND", name: "Tunisian Dinar" },
  { code: "TRY", name: "Turkish Lira" },
  { code: "TMT", name: "Turkmenistan Manat" },
  { code: "UGX", name: "Ugandan Shilling" },
  { code: "UAH", name: "Ukrainian Hryvnia" },
  { code: "AED", name: "United Arab Emirates Dirham" },
  { code: "GBP", name: "United Kingdom Pound" },
  { code: "USD", name: "United States Dollar" },
  { code: "UYU", name: "Uruguayan Peso" },
  { code: "UZS", name: "Uzbekistani Soʻm" },
  { code: "VUV", name: "Vanuatu Vatu" },
  { code: "VND", name: "Vietnamese Đồng" },
  { code: "XOF", name: "West African CFA Franc" },
  { code: "YER", name: "Yemeni Rial" },
  { code: "ZMW", name: "Zambian Kwacha" },
  { code: "ZWL", name: "Zimbabwean Dollar" },
];

// Filtrer les devises en fonction de la recherche
const filteredCurrencies = currencies.filter(curr => 
  curr.code.toLowerCase().includes(paymentCurrencySearch.toLowerCase()) ||
  curr.name.toLowerCase().includes(paymentCurrencySearch.toLowerCase())
);

  // Active tab
  const [activeTab, setActiveTab] = useState<"details" | "documents" | "payments" | "timeline" | "communications">("details");

  const fetchApplicationDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get<VisaApplicationDetail>(`${API_URL}/visa-applications/${id}`);
      const data = response.data;
      setApplication(data);
      setStatus(data.status || "pending");
      setAdminNotes(data.admin_notes || "");
    } catch (error) {
      console.error("Error fetching application details:", error);
      toast.error("Failed to load application details.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchApplicationDetails();
  }, [id, fetchApplicationDetails]);

  // Handlers
  const handleStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application) return;
    try {
      setIsSubmitting(true);
      await axios.patch(`${API_URL}/visa-applications/${application.id}/status`, {
        status: status,
        admin_notes: adminNotes,
      });
      toast.success("Status updated successfully!");
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update status.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application || !newNote.trim()) {
      toast.warning("Note content is required.");
      return;
    }
    try {
      setIsSubmitting(true);
      await axios.post(`${API_URL}/visa-applications/${application.id}/notes`, {
        content: newNote,
      });
      toast.success("Note added successfully!");
      setShowNoteModal(false);
      setNewNote("");
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to add note.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application || !paymentAmount) {
      toast.warning("Amount is required.");
      return;
    }
    try {
      setIsSubmitting(true);
      await axios.post(`${API_URL}/visa-applications/${application.id}/payments`, {
        amount: parseFloat(paymentAmount),
        currency: paymentCurrency,
        payment_method: paymentMethod,
        transaction_id: paymentTransactionId,
        status: paymentStatus,
        description: paymentDescription,
      });
      toast.success("Payment recorded successfully!");
      setShowPaymentModal(false);
      setPaymentAmount("");
      setPaymentCurrency("USD");
      setPaymentMethod("cash");
      setPaymentTransactionId("");
      setPaymentStatus("completed");
      setPaymentDescription("");
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to record payment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application || !emailSubject.trim() || !emailMessage.trim()) {
      toast.warning("Subject and message are required.");
      return;
    }
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("subject", emailSubject);
      formData.append("message", emailMessage);
      emailAttachments.forEach((file) => formData.append("attachments[]", file));
      await axios.post(`${API_URL}/visa-applications/${application.id}/communications`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Email sent successfully!");
      setShowEmailModal(false);
      setEmailSubject("");
      setEmailMessage("");
      setEmailAttachments([]);
      setEmailTemplate("custom");
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to send email.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    const files = fileInputRef.current?.files;
    if (!files || files.length === 0) {
      toast.warning("Please select files to upload.");
      return;
    }
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("document_type", uploadDocumentType);
      Array.from(files).forEach((file) => formData.append("documents[]", file));
      await axios.post(`${API_URL}/visa-applications/${application?.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documents uploaded successfully!");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to upload documents.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = (documentId: number) => {
    setDocumentToDelete(documentId);
    setShowDeleteDocumentModal(true);
  };

  const confirmDeleteDocument = async () => {
  if (!documentToDelete || !application) return;
  try {
    await axios.delete(`${API_URL}/visa-applications/${application.id}/documents/${documentToDelete}`);
    toast.success("Document removed successfully!");
    fetchApplicationDetails();
  } catch (error: any) {
    toast.error("Failed to delete document.");
  } finally {
    setShowDeleteDocumentModal(false);
    setDocumentToDelete(null);
  }
};

  const handleIssueVisa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application) return;
    try {
      setIsSubmitting(true);
      await axios.post(`${API_URL}/visa-applications/${application.id}/issue-visa`, {
        visa_number: visaNumber,
        issue_date: visaIssueDate,
        expiry_date: visaExpiryDate,
      });
      toast.success("Visa issued successfully!");
      setShowVisaModal(false);
      setVisaNumber("");
      setVisaIssueDate("");
      setVisaExpiryDate("");
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to issue visa.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application) return;
    try {
      setIsSubmitting(true);
      await axios.post(`${API_URL}/visa-applications/${application.id}/appointment`, {
        appointment_date: appointmentDate,
        location: appointmentLocation,
        notes: appointmentNotes,
      });
      toast.success("Appointment scheduled successfully!");
      setShowAppointmentModal(false);
      setAppointmentDate("");
      setAppointmentLocation("");
      setAppointmentNotes("");
      fetchApplicationDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to schedule appointment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!application) return;
    try {
      await axios.delete(`${API_URL}/visa-applications/${application.id}`);
      toast.success("Application deleted successfully.");
      navigate("/admin/visa-applications");
    } catch (error: any) {
      toast.error("Failed to delete application.");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const getEmailTemplate = (type: string) => {
    switch (type) {
      case "document_request":
        return {
          subject: `Additional Documents Required - ${application?.reference_number}`,
          message: `Dear ${application?.firstname} ${application?.lastname},\n\nWe have reviewed your visa application (${application?.reference_number}) and require additional documents to proceed.\n\nPlease submit the requested documents at your earliest convenience.\n\nBest regards,\nVisa Processing Team`,
        };
      case "approval":
        return {
          subject: `Visa Application Approved - ${application?.reference_number}`,
          message: `Dear ${application?.firstname} ${application?.lastname},\n\nCongratulations! Your visa application (${application?.reference_number}) has been approved.\n\nBest regards,\nVisa Processing Team`,
        };
      case "rejection":
        return {
          subject: `Update on Your Visa Application - ${application?.reference_number}`,
          message: `Dear ${application?.firstname} ${application?.lastname},\n\nWe regret to inform you that your visa application (${application?.reference_number}) could not be processed at this time.\n\nPlease contact our office for more details.\n\nBest regards,\nVisa Processing Team`,
        };
      default:
        return { subject: "", message: "" };
    }
  };

  const handleEmailTemplateChange = (template: string) => {
    setEmailTemplate(template);
    if (template !== "custom") {
      const data = getEmailTemplate(template);
      setEmailSubject(data.subject);
      setEmailMessage(data.message);
    } else {
      setEmailSubject("");
      setEmailMessage("");
    }
  };

  // Helper functions
  const getStatusBadgeStyle = (statusName: string) => {
    const styles: Record<string, { bg: string; color: string; border: string; icon: string }> = {
      pending: { bg: "#fefce8", color: "#a16207", border: "#fef08a", icon: "fa-clock" },
      under_review: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", icon: "fa-magnifying-glass" },
      documents_requested: { bg: "#f0f4f8", color: "#475569", border: "#cbd5e1", icon: "fa-file-circle-plus" },
      approved: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", icon: "fa-circle-check" },
      rejected: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", icon: "fa-circle-xmark" },
    };
    return styles[statusName] || { bg: "#f8fafc", color: "#475569", border: "#e2e8f0", icon: "fa-circle-question" };
  };

  const getActivityColor = (actionType: string): string => {
    const colors: Record<string, string> = {
      status_change: "#3b82f6",
      document_upload: "#10b981",
      document_delete: "#ef4444",
      email_sent: "#6366f1",
      note_added: "#64748b",
      visa_issued: "#059669",
      payment_recorded: "#06b6d4",
      appointment_scheduled: "#0ea5e9",
      internal_review: "#294b5a",
    };
    return colors[actionType] || "#64748b";
  };

  const getActivityIcon = (actionType: string): string => {
    const icons: Record<string, string> = {
      status_change: "fa-arrows-rotate",
      document_upload: "fa-upload",
      document_delete: "fa-trash-can",
      email_sent: "fa-envelope",
      note_added: "fa-note-sticky",
      visa_issued: "fa-passport",
      payment_recorded: "fa-credit-card",
      appointment_scheduled: "fa-calendar-check",
      internal_review: "fa-clipboard-check",
    };
    return icons[actionType] || "fa-circle-info";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const getDocumentIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) return "fa-file-pdf";
    if (mimeType.includes("image")) return "fa-file-image";
    if (mimeType.includes("word") || mimeType.includes("document")) return "fa-file-word";
    return "fa-file";
  };

  const getPaymentStatusBadge = (paymentStatus: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      completed: { bg: "#f0fdf4", color: "#15803d" },
      pending: { bg: "#fefce8", color: "#a16207" },
      failed: { bg: "#fef2f2", color: "#b91c1c" },
      refunded: { bg: "#f0f4f8", color: "#475569" },
    };
    return styles[paymentStatus] || { bg: "#f8fafc", color: "#475569" };
  };

  function SkeletonLoader() {
  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar"><SidebarAdmin /></aside>
      <div className="dashboard-main" style={{ padding: "20px", backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>

        {/* Header Skeleton */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ 
            width: 120, 
            height: 14,
            marginBottom: 12,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div style={{ 
                width: 280, 
                height: 32,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ 
                width: 120, 
                height: 28,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 20,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <div style={{ 
                width: 140, 
                height: 38,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ 
                width: 120, 
                height: 38,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "20px", borderBottom: "1px solid #e2e8f0", overflowX: "auto" }}>
          {[1, 2, 3, 4, 5].map((_, i) => (
            <div key={i} style={{ 
              padding: "10px 18px",
              borderBottom: "2px solid transparent",
            }}>
              <div style={{ 
                width: i === 0 ? 140 : i === 1 ? 130 : i === 2 ? 120 : i === 3 ? 90 : 110,
                height: 18,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 4,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          ))}
        </div>

        {/* Main Content Skeleton */}
        <div className="row y-gap-20">
          <div className="col-12 col-lg-8">
            {/* Applicant Info Skeleton */}
            <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px", marginBottom: "20px" }}>
              <div style={{ 
                width: 180, 
                height: 18,
                marginBottom: 16,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 4,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div className="row y-gap-15">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div style={{ 
                      width: i === 0 ? 80 : i === 1 ? 60 : i === 2 ? 80 : i === 3 ? 80 : i === 4 ? 90 : i === 5 ? 70 : 60,
                      height: 12,
                      marginBottom: 6,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 3,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{ 
                      width: i === 0 ? 160 : i === 1 ? 200 : i === 2 ? 140 : i === 3 ? 130 : i === 4 ? 120 : i === 5 ? 100 : 180,
                      height: 16,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes Section Skeleton */}
            <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" }}>
              <div className="d-flex justify-content-between align-items-center mb-20" style={{ borderBottom: "1px solid #edf2f7", paddingBottom: "12px" }}>
                <div style={{ 
                  width: 140, 
                  height: 18,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{ 
                  width: 100, 
                  height: 32,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 6,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
              {[...Array(2)].map((_, i) => (
                <div key={i} style={{ 
                  padding: "16px", 
                  borderRadius: "8px", 
                  backgroundColor: "#f8fafc", 
                  border: "1px solid #e2e8f0", 
                  borderLeft: "3px solid #294b5a",
                  marginBottom: i === 0 ? 12 : 0
                }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div style={{ 
                      width: 80, 
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 3,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{ 
                      width: 120, 
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 3,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                  <div style={{ 
                    width: "80%", 
                    height: 14,
                    marginBottom: 8,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ 
                    width: "60%", 
                    height: 14,
                    marginBottom: 8,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ 
                      width: 24, 
                      height: 24,
                      borderRadius: "50%",
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{ 
                      width: 100, 
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 3,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Sidebar Skeleton */}
          <div className="col-12 col-lg-4">
            <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px", position: "sticky", top: "20px" }}>
              <div style={{ 
                width: 140, 
                height: 18,
                marginBottom: 16,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 4,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div className="mb-15">
                <div style={{ 
                  width: 60, 
                  height: 13,
                  marginBottom: 6,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 3,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{ 
                  width: "100%", 
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
              <div className="mb-20">
                <div style={{ 
                  width: 80, 
                  height: 13,
                  marginBottom: 6,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 3,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{ 
                  width: "100%", 
                  height: 80,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
              <div style={{ 
                width: "100%", 
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!application) {
    return (
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar"><SidebarAdmin /></aside>
        <div className="dashboard-main p-30">
          <div className="alert alert-danger">Visa application not found.</div>
          <button onClick={() => navigate(-1)} className="button -blue-1 bg-blue-1 text-white px-20 py-10 rounded-8 mt-20">
            Back
          </button>
        </div>
      </div>
    );
  }

  const currentBadge = getStatusBadgeStyle(application.status);

  return (
    <>
    <ToastContainer position="top-right" autoClose={3000} />
      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px", boxSizing: "border-box", color: "#1e293b" }}>
        
        {/* ========== HEADER ========== */}
        <div style={{ marginBottom: "24px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{ border: "none", background: "none", color: "#64748b", fontSize: "14px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "12px", padding: 0 }}
          >
            <i className="fa-solid fa-arrow-left"></i> Back to list
          </button>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <h1 className="text-22 fw-700" style={{ color: "#1e293b", margin: 0 }}>
                Application: <span style={{ color: "#294b5a" }}>{application.reference_number}</span>
              </h1>
              <span style={{ padding: "4px 16px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, backgroundColor: currentBadge.bg, color: currentBadge.color, border: `1px solid ${currentBadge.border}`, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                <i className={`fa-solid ${currentBadge.icon} me-1`}></i>
                {application.status.replace(/_/g, " ")}
              </span>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button onClick={() => { setEmailTemplate("custom"); setShowEmailModal(true); }} style={{ backgroundColor: "#294b5a", color: "#ffffff", border: "none", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
                <i className="fa-solid fa-envelope"></i> Send Email
              </button>

              <div style={{ position: "relative" }}>
                <button onClick={(e) => { const dropdown = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement; if (dropdown) dropdown.style.display = dropdown.style.display === "block" ? "none" : "block"; }} style={{ backgroundColor: "#ffffff", color: "#1e293b", border: "1px solid #cbd5e1", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
                  <i className="fa-solid fa-bolt"></i> Actions
                  <i className="fa-solid fa-chevron-down" style={{ fontSize: "10px" }}></i>
                </button>
                <div style={{ display: "none", position: "absolute", right: 0, top: "100%", marginTop: "4px", backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, minWidth: "220px", overflow: "hidden" }} className="actions-dropdown">
                  <button onClick={() => { setShowVisaModal(true); (document.querySelector('.actions-dropdown') as HTMLElement).style.display = 'none'; }} style={{ width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", fontSize: "13px", color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-solid fa-passport" style={{ color: "#059669" }}></i> Issue Visa
                  </button>
                  <button onClick={() => { setShowAppointmentModal(true); (document.querySelector('.actions-dropdown') as HTMLElement).style.display = 'none'; }} style={{ width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", fontSize: "13px", color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-solid fa-calendar-plus" style={{ color: "#0ea5e9" }}></i> Schedule Appointment
                  </button>
                  <button onClick={() => { setShowPaymentModal(true); (document.querySelector('.actions-dropdown') as HTMLElement).style.display = 'none'; }} style={{ width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", fontSize: "13px", color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-solid fa-credit-card" style={{ color: "#06b6d4" }}></i> Record Payment
                  </button>
                  <button onClick={() => { setShowNoteModal(true); (document.querySelector('.actions-dropdown') as HTMLElement).style.display = 'none'; }} style={{ width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", fontSize: "13px", color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-solid fa-clipboard-list" style={{ color: "#64748b" }}></i> Add Note
                  </button>
                  <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }}></div>
                  <button onClick={() => { setShowDeleteModal(true); (document.querySelector('.actions-dropdown') as HTMLElement).style.display = 'none'; }} style={{ width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", fontSize: "13px", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-solid fa-trash-can"></i> Delete Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========== TABS ========== */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "20px", borderBottom: "1px solid #e2e8f0", overflowX: "auto" }}>
          {[
            { key: "details", icon: "fa-user", label: "Applicant Details" },
            { key: "documents", icon: "fa-folder-open", label: `Documents (${application.documents?.length || 0})` },
            { key: "payments", icon: "fa-credit-card", label: `Payments (${application.payments?.length || 0})` },
            { key: "timeline", icon: "fa-clock-rotate-left", label: "Timeline" },
            { key: "communications", icon: "fa-comments", label: "Messages" },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} style={{ padding: "10px 18px", border: "none", borderBottom: activeTab === tab.key ? "2px solid #294b5a" : "2px solid transparent", backgroundColor: "transparent", color: activeTab === tab.key ? "#294b5a" : "#64748b", fontSize: "13.5px", fontWeight: activeTab === tab.key ? 600 : 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s" }}>
              <i className={`fa-solid ${tab.icon} me-2`}></i>{tab.label}
            </button>
          ))}
        </div>

        {/* ========== CONTENT ========== */}
        <div className="row y-gap-20">
          <div className="col-12 col-lg-8">
            
            {/* DETAILS TAB */}
            {activeTab === "details" && (
              <>
                <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px", marginBottom: "20px" }}>
      <h2 className="text-16 fw-600 mb-20" style={{ color: "#1e293b", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
        <i className="fa-solid fa-user me-2" style={{ color: "#294b5a" }}></i>Applicant Information
      </h2>
      
      {/* Grid 2 colonnes pour les infos */}
      <div className="row y-gap-15">
        <div className="col-12 col-md-6">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-user me-1" style={{ fontSize: "10px" }}></i> Full Name
            </span>
            <span style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a" }}>{application.firstname} {application.lastname}</span>
          </div>
        </div>
        
        <div className="col-12 col-md-6">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-envelope me-1" style={{ fontSize: "10px" }}></i> Email
            </span>
            <a href={`mailto:${application.email}`} style={{ fontSize: "14px", fontWeight: 500, color: "#294b5a", textDecoration: "none" }}>
              {application.email}
            </a>
          </div>
        </div>
        
        <div className="col-12 col-md-6">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-phone me-1" style={{ fontSize: "10px" }}></i> Contact
            </span>
            <a href={`tel:${application.contact_no}`} style={{ fontSize: "14px", fontWeight: 500, color: "#294b5a", textDecoration: "none" }}>
              {application.contact_no}
            </a>
          </div>
        </div>
        
        <div className="col-12 col-md-6">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-passport me-1" style={{ fontSize: "10px" }}></i> Passport
            </span>
            <span style={{ fontSize: "14px", fontWeight: 600, fontFamily: "monospace", color: "#294b5a", letterSpacing: "1px" }}>
              {application.passport_no}
            </span>
          </div>
        </div>
        
        <div className="col-12 col-md-6">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-flag me-1" style={{ fontSize: "10px" }}></i> Nationality
            </span>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "#0f172a", textTransform: "capitalize" }}>
              {application.nationality}
            </span>
          </div>
        </div>
        
        <div className="col-12 col-md-6">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              Gender
            </span>
            <span style={{ 
              fontSize: "14px", 
              fontWeight: 500, 
              color: "#0f172a", 
              textTransform: "capitalize",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}>
              <span style={{ 
                display: "inline-block",
                padding: "2px 12px",
                borderRadius: "12px",
                backgroundColor: application.gender?.toLowerCase() === "male" ? "#eff6ff" : "#fdf2f8",
                color: application.gender?.toLowerCase() === "male" ? "#1d4ed8" : "#be185d",
                fontSize: "12px",
                fontWeight: 600
              }}>
                {application.gender}
              </span>
            </span>
          </div>
        </div>
        
        <div className="col-12">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-bullseye me-1" style={{ fontSize: "10px" }}></i> Purpose of Visit
            </span>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "#0f172a", textTransform: "capitalize" }}>
              {application.purpose_of_visit}
            </span>
          </div>
        </div>
        
        <div className="col-12">
          <div style={{ 
            backgroundColor: "#f8fafc", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s ease"
          }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "4px" }}>
              <i className="fa-solid fa-location-dot me-1" style={{ fontSize: "10px" }}></i> Address
            </span>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "#0f172a" }}>
              {application.current_address}, {application.city} {application.state || ""} {application.zipcode || ""}
            </span>
          </div>
        </div>
      </div>

      {/* Visa Pack avec OMR */}
      {application.visa_pack && (
        <div style={{ 
          marginTop: "20px", 
          paddingTop: "16px", 
          borderTop: "1px solid #edf2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          padding: "12px 16px"
        }}>
          <div>
            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block" }}>
              <i className="fa-solid fa-passport" style={{ marginRight: "4px" }}></i> Visa Pack
            </span>
            <span style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b" }}>
              {application.visa_pack.name_en || application.visa_pack.name_ar || "N/A"}
            </span>
          </div>
          {application.visa_pack.price && (
            <div style={{ 
              backgroundColor: "#294b5a", 
              color: "#ffffff", 
              padding: "6px 16px", 
              borderRadius: "20px",
              fontSize: "14px",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: "0 2px 8px rgba(41,75,90,0.2)"
            }}>
              <span style={{ fontSize: "10px", opacity: 0.8, fontWeight: 600 }}>OMR</span>
              <span>{Number(application.visa_pack.price).toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Visa Details */}
      {application.visa_number && (
        <div style={{ 
          marginTop: "16px", 
          paddingTop: "16px", 
          borderTop: "1px solid #edf2f7"
        }}>
          <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "8px" }}>
            <i className="fa-solid fa-circle-check me-1" style={{ color: "#059669" }}></i> Visa Details
          </span>
          <div className="row g-2">
            <div className="col-12 col-md-4">
              <div style={{ 
                backgroundColor: "#f0fdf4", 
                borderRadius: "6px", 
                padding: "6px 12px",
                border: "1px solid #bbf7d0"
              }}>
                <span style={{ fontSize: "10px", color: "#15803d", display: "block" }}>Visa Number</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#059669", fontFamily: "monospace" }}>
                  {application.visa_number}
                </span>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div style={{ 
                backgroundColor: "#f8fafc", 
                borderRadius: "6px", 
                padding: "6px 12px",
                border: "1px solid #e2e8f0"
              }}>
                <span style={{ fontSize: "10px", color: "#94a3b8", display: "block" }}>Issue Date</span>
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
                  {application.visa_issue_date ? formatDate(application.visa_issue_date).split(",")[0] : "N/A"}
                </span>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div style={{ 
                backgroundColor: "#fef2f2", 
                borderRadius: "6px", 
                padding: "6px 12px",
                border: "1px solid #fecaca"
              }}>
                <span style={{ fontSize: "10px", color: "#dc2626", display: "block" }}>Expiry Date</span>
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#b91c1c" }}>
                  {application.visa_expiry_date ? formatDate(application.visa_expiry_date).split(",")[0] : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment */}
      {application.appointment_date && (
        <div style={{ 
          marginTop: "16px", 
          paddingTop: "16px", 
          borderTop: "1px solid #edf2f7"
        }}>
          <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", display: "block", marginBottom: "8px" }}>
            <i className="fa-solid fa-calendar-check me-1" style={{ color: "#0ea5e9" }}></i> Appointment
          </span>
          <div style={{ 
            backgroundColor: "#eff6ff", 
            borderRadius: "8px", 
            padding: "12px 16px",
            border: "1px solid #bfdbfe",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px"
          }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e40af" }}>
              <i className="fa-solid fa-clock me-2"></i>
              {formatDate(application.appointment_date)}
            </span>
            {application.appointment_location && (
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                <i className="fa-solid fa-location-dot me-1"></i>
                {application.appointment_location}
              </span>
            )}
          </div>
        </div>
      )}
    </div>

                {/* Notes Section */}
                <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" }}>
                  <div className="d-flex justify-content-between align-items-center mb-20" style={{ borderBottom: "1px solid #edf2f7", paddingBottom: "12px" }}>
                    <h2 className="text-16 fw-600" style={{ color: "#1e293b", margin: 0 }}><i className="fa-solid fa-clipboard-list me-2" style={{ color: "#294b5a" }}></i>Internal Notes</h2>
                    <button onClick={() => setShowNoteModal(true)} style={{ backgroundColor: "#294b5a", color: "#ffffff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <i className="fa-solid fa-plus"></i> Add Note
                    </button>
                  </div>
                  {application.notes && application.notes.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {application.notes.map((note, index) => (
                        <div key={note.id} style={{ padding: "16px", borderRadius: "8px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "3px solid #294b5a" }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>Note #{application.notes.length - index}</span>
                            <span style={{ fontSize: "11px", color: "#94a3b8" }}>{formatDate(note.created_at)}</span>
                          </div>
                          <p style={{ fontSize: "13.5px", color: "#334155", margin: "0 0 8px 0", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>{note.content}</p>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "24px", height: "24px", borderRadius: "50%", backgroundColor: "#294b5a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <i className="fa-solid fa-user-tie" style={{ fontSize: "10px", color: "#ffffff" }}></i>
                            </div>
                            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>{note.created_by_name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px 20px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                      <i className="fa-solid fa-clipboard" style={{ fontSize: "32px", color: "#cbd5e1", marginBottom: "12px", display: "block" }}></i>
                      <p style={{ fontSize: "14px", color: "#64748b", margin: "0 0 4px 0", fontWeight: 500 }}>No internal notes yet</p>
                      <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0 }}>Add notes to keep track of important information</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* DOCUMENTS TAB */}
            {activeTab === "documents" && (
              <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" }}>
                <h2 className="text-16 fw-600 mb-20" style={{ color: "#1e293b", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
                  <i className="fa-solid fa-folder-open me-2" style={{ color: "#294b5a" }}></i>Dossier Documents
                </h2>
                <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", padding: "16px", marginBottom: "20px" }}>
                  <form onSubmit={handleFileUpload}>
                    <div className="row g-3 align-items-end">
                      <div className="col-12 col-md-4">
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "4px" }}>Document Type</label>
                        <select value={uploadDocumentType} onChange={(e) => setUploadDocumentType(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#ffffff" }}>
                          <option value="additional_document">Additional Document</option>
                          <option value="passport_copy">Passport Copy</option>
                          <option value="photo">Photo</option>
                          <option value="flight_ticket">Flight Ticket</option>
                          <option value="hotel_booking">Hotel Booking</option>
                          <option value="bank_statement">Bank Statement</option>
                          <option value="insurance">Insurance</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-5">
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "4px" }}>Select Files</label>
                        <input ref={fileInputRef} type="file" multiple style={{ width: "100%", padding: "7px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#ffffff" }} />
                      </div>
                      <div className="col-12 col-md-3">
                        <button type="submit" disabled={isUploading} style={{ width: "100%", backgroundColor: "#294b5a", color: "#ffffff", border: "none", borderRadius: "6px", padding: "9px", fontSize: "13px", fontWeight: 600, cursor: isUploading ? "not-allowed" : "pointer", opacity: isUploading ? 0.7 : 1 }}>
                          {isUploading ? <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Uploading...</> : <><i className="fa-solid fa-upload me-1"></i>Upload</>}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                {application.documents && application.documents.length > 0 ? (
                  <div className="row g-3">
                    {application.documents.map((doc) => (
                      <div key={doc.id} className="col-12 col-md-6">
                        <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", backgroundColor: "#ffffff", display: "flex", alignItems: "center", gap: "12px" }}>
                          <div style={{ width: "40px", height: "40px", borderRadius: "8px", backgroundColor: "#f0f4f8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className={`fa-solid ${getDocumentIcon(doc.mime_type)}`} style={{ fontSize: "18px", color: "#294b5a" }}></i>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#294b5a", display: "block" }}>{doc.document_type.replace(/_/g, " ")}</span>
                            <span style={{ fontSize: "13px", color: "#334155", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.original_name}>{doc.original_name}</span>
                            <span style={{ fontSize: "11px", color: "#94a3b8" }}>{formatFileSize(doc.size)} • {doc.uploaded_by_type === "admin" ? "Admin" : "Client"} • {formatDate(doc.created_at).split(",")[0]}</span>
                          </div>
                          <div className="d-flex gap-1">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: "#294b5a", color: "#ffffff", padding: "6px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}><i className="fa-solid fa-eye"></i></a>
                            <button 
  onClick={() => handleDeleteDocument(doc.id)} 
  style={{ backgroundColor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "6px 10px", borderRadius: "6px", fontSize: "11px", cursor: "pointer" }}
>
  <i className="fa-solid fa-trash"></i>
</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
                    <i className="fa-solid fa-file-circle-plus" style={{ fontSize: "36px", marginBottom: "12px", display: "block" }}></i>
                    <p style={{ fontSize: "14px", margin: 0 }}>No documents in this dossier.</p>
                  </div>
                )}
              </div>
            )}

            {/* PAYMENTS TAB */}
            {activeTab === "payments" && (
              <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" }}>
                <div className="d-flex justify-content-between align-items-center mb-20" style={{ borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
                  <h2 className="text-16 fw-600" style={{ color: "#1e293b", margin: 0 }}><i className="fa-solid fa-credit-card me-2" style={{ color: "#294b5a" }}></i>Payment Records</h2>
                  <button onClick={() => setShowPaymentModal(true)} style={{ backgroundColor: "#294b5a", color: "#ffffff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa-solid fa-plus"></i> Record Payment
                  </button>
                </div>
                {application.payments && application.payments.length > 0 ? (
                  <>
                    <div style={{ backgroundColor: "#f0f4f8", borderRadius: "8px", padding: "16px", marginBottom: "20px", border: "1px solid #cbd5e1" }}>
                      <div className="row">
                        <div className="col-6"><span style={{ fontSize: "12px", color: "#64748b" }}>Total Received</span><span style={{ fontSize: "20px", fontWeight: 700, color: "#059669", display: "block" }}>OMR {application.payments.filter(p => p.status === "completed").reduce((sum, p) => sum + Number(p.amount), 0).toFixed(2)}</span></div>
                        <div className="col-6"><span style={{ fontSize: "12px", color: "#64748b" }}>Transactions</span><span style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", display: "block" }}>{application.payments.length}</span></div>
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                        <thead><tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                          <th style={{ padding: "10px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>Date</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>Description</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>Method</th>
                          <th style={{ padding: "10px 12px", textAlign: "right", color: "#64748b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>Amount</th>
                          <th style={{ padding: "10px 12px", textAlign: "center", color: "#64748b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>Status</th>
                        </tr></thead>
                        <tbody>
                          {application.payments.map((payment) => {
                            const badge = getPaymentStatusBadge(payment.status);
                            return (
                              <tr key={payment.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                <td style={{ padding: "10px 12px", color: "#64748b", fontSize: "12px" }}>{formatDate(payment.created_at).split(",")[0]}</td>
                                <td style={{ padding: "10px 12px", color: "#1e293b", fontWeight: 500 }}>{payment.description || "Payment"}{payment.transaction_id && <span style={{ display: "block", fontSize: "11px", color: "#94a3b8", fontFamily: "monospace" }}>ID: {payment.transaction_id}</span>}</td>
                                <td style={{ padding: "10px 12px", color: "#64748b", textTransform: "capitalize", fontSize: "12px" }}>{payment.payment_method.replace(/_/g, " ")}</td>
                                <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: "#1e293b" }}>{payment.currency} {Number(payment.amount).toFixed(2)}</td>
                                <td style={{ padding: "10px 12px", textAlign: "center" }}><span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600, backgroundColor: badge.bg, color: badge.color, textTransform: "capitalize" }}>{payment.status}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
                    <i className="fa-solid fa-receipt" style={{ fontSize: "36px", marginBottom: "12px", display: "block" }}></i>
                    <p style={{ fontSize: "14px", margin: 0 }}>No payment records yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* TIMELINE TAB */}
            {activeTab === "timeline" && (
              <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" }}>
                <h2 className="text-16 fw-600 mb-20" style={{ color: "#1e293b", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
                  <i className="fa-solid fa-clock-rotate-left me-2" style={{ color: "#294b5a" }}></i>Activity Timeline
                </h2>
                {application.activities && application.activities.length > 0 ? (
                  <div style={{ position: "relative", paddingLeft: "28px" }}>
                    <div style={{ position: "absolute", left: "11px", top: "8px", bottom: "8px", width: "2px", backgroundColor: "#e2e8f0" }}></div>
                    {application.activities.map((activity, index) => (
                      <div key={activity.id} style={{ position: "relative", marginBottom: index === application.activities.length - 1 ? "0" : "20px" }}>
                        <div style={{ position: "absolute", left: "-22px", top: "4px", width: "24px", height: "24px", borderRadius: "50%", backgroundColor: getActivityColor(activity.action_type), border: "3px solid #ffffff", boxShadow: "0 0 0 2px #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className={`fa-solid ${getActivityIcon(activity.action_type)}`} style={{ fontSize: "10px", color: "#ffffff" }}></i>
                        </div>
                        <div style={{ backgroundColor: index === 0 ? "#f8fafc" : "transparent", borderRadius: "8px", padding: index === 0 ? "12px" : "0", border: index === 0 ? "1px solid #e2e8f0" : "none" }}>
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>{activity.action}{index === 0 && <span style={{ marginLeft: "8px", fontSize: "10px", padding: "2px 8px", backgroundColor: "#dbeafe", color: "#1e40af", borderRadius: "10px", fontWeight: 700 }}>LATEST</span>}</span>
                            <span style={{ fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap" }}>{formatDate(activity.created_at)}</span>
                          </div>
                          <p style={{ fontSize: "12.5px", color: "#64748b", margin: "6px 0 8px 0", lineHeight: "1.5" }}>{activity.description}</p>
                          <span style={{ fontSize: "11px", padding: "2px 10px", borderRadius: "10px", backgroundColor: activity.performed_by_type === "admin" ? "#f0f4f8" : activity.performed_by_type === "system" ? "#f8fafc" : "#eff6ff", color: activity.performed_by_type === "admin" ? "#294b5a" : activity.performed_by_type === "system" ? "#64748b" : "#3b82f6", fontWeight: 600 }}>
                            <i className={`fa-solid ${activity.performed_by_type === "admin" ? "fa-user-tie" : activity.performed_by_type === "system" ? "fa-robot" : "fa-user"} me-1`}></i>{activity.performed_by_name}
                          </span>
                          {activity.metadata?.old_status && activity.metadata?.new_status && (
                            <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "6px", fontSize: "11px" }}>
                              <span style={{ padding: "2px 8px", borderRadius: "4px", backgroundColor: getStatusBadgeStyle(activity.metadata.old_status).bg, color: getStatusBadgeStyle(activity.metadata.old_status).color, fontWeight: 600 }}>{activity.metadata.old_status.replace(/_/g, " ")}</span>
                              <i className="fa-solid fa-arrow-right" style={{ color: "#94a3b8", fontSize: "10px" }}></i>
                              <span style={{ padding: "2px 8px", borderRadius: "4px", backgroundColor: getStatusBadgeStyle(activity.metadata.new_status).bg, color: getStatusBadgeStyle(activity.metadata.new_status).color, fontWeight: 600 }}>{activity.metadata.new_status.replace(/_/g, " ")}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
                    <i className="fa-solid fa-clock" style={{ fontSize: "36px", marginBottom: "12px", display: "block" }}></i>
                    <p style={{ fontSize: "14px", margin: 0 }}>No activity recorded yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* COMMUNICATIONS TAB */}
            {activeTab === "communications" && (
              <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" }}>
                <div className="d-flex justify-content-between align-items-center mb-20" style={{ borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
                  <h2 className="text-16 fw-600" style={{ color: "#1e293b", margin: 0 }}><i className="fa-solid fa-comments me-2" style={{ color: "#294b5a" }}></i>Communication History</h2>
                  <button onClick={() => { setEmailTemplate("custom"); setShowEmailModal(true); }} style={{ backgroundColor: "#294b5a", color: "#ffffff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa-solid fa-plus"></i> Send Message
                  </button>
                </div>
                {application.communications && application.communications.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {application.communications.map((comm) => (
                      <div key={comm.id} style={{ padding: "14px", borderRadius: "8px", backgroundColor: comm.sender_type === "admin" ? "#f0f4f8" : "#f8fafc", border: `1px solid ${comm.sender_type === "admin" ? "#cbd5e1" : "#e2e8f0"}` }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>{comm.subject}</span>
                          <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", backgroundColor: comm.sender_type === "admin" ? "#294b5a" : "#64748b", color: "#ffffff", whiteSpace: "nowrap", flexShrink: 0, marginLeft: "8px" }}>{comm.sender_type === "admin" ? "Sent" : "Received"}</span>
                        </div>
                        <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 8px 0", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>{comm.message}</p>
                        {comm.attachments && comm.attachments.length > 0 && (
                          <div style={{ marginBottom: "8px" }}>
                            {comm.attachments.map((att: any, idx: number) => (
                              <a key={idx} href={att.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e2e8f0", fontSize: "11px", color: "#294b5a", textDecoration: "none", marginRight: "4px", marginBottom: "4px" }}>
                                <i className="fa-solid fa-paperclip" style={{ fontSize: "10px" }}></i>{att.name}
                              </a>
                            ))}
                          </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                          <span style={{ fontSize: "11px", color: "#94a3b8" }}>{comm.sender_name}</span>
                          <span style={{ fontSize: "11px", color: "#94a3b8" }}>{formatDate(comm.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
                    <i className="fa-solid fa-envelope-open-text" style={{ fontSize: "36px", marginBottom: "12px", display: "block" }}></i>
                    <p style={{ fontSize: "14px", margin: 0 }}>No communications yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ========== RIGHT SIDEBAR ========== */}
          <div className="col-12 col-lg-4">
            <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px", position: "sticky", top: "20px" }}>
              <h3 className="text-15 fw-600 mb-15" style={{ color: "#1e293b" }}>
                <i className="fa-solid fa-gear me-2" style={{ color: "#294b5a" }}></i>Update Status
              </h3>
              <form onSubmit={handleStatusUpdate}>
                <div className="mb-15">
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13.5px", outline: "none", backgroundColor: "#f8fafc", cursor: "pointer" }}>
                    <option value="pending">Pending</option>
                    <option value="under_review">Under Review</option>
                    <option value="documents_requested">Documents Requested</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="mb-20">
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Admin Notes</label>
                  <textarea rows={3} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Internal notes..." style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc", resize: "vertical" }} />
                </div>
                <button type="submit" disabled={isSubmitting} style={{ width: "100%", backgroundColor: "#294b5a", color: "#ffffff", border: "none", borderRadius: "8px", padding: "11px", fontSize: "13.5px", fontWeight: 600, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.7 : 1 }}>
                  {isSubmitting ? <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Updating...</> : <><i className="fa-solid fa-floppy-disk me-1"></i>Save Status</>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ========== MODALS ========== */}
      
      {/* Note Modal */}
      {showNoteModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: "16px" }} onClick={() => !isSubmitting && setShowNoteModal(false)}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "520px", padding: "24px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }} onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-20">
              <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 }}><i className="fa-solid fa-clipboard-list me-2" style={{ color: "#294b5a" }}></i>Add Internal Note</h3>
              <button onClick={() => !isSubmitting && setShowNoteModal(false)} style={{ border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: "18px" }}><i className="fa-solid fa-times"></i></button>
            </div>
            <form onSubmit={handleAddNote}>
              <div className="mb-20">
                <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "8px", display: "block" }}>Note Content</label>
                <textarea rows={6} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Write your internal note here..." required autoFocus style={{ width: "100%", padding: "14px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13.5px", outline: "none", resize: "vertical", backgroundColor: "#ffffff", fontFamily: "inherit", lineHeight: "1.6" }} />
                <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#94a3b8" }}>
                  <i className="fa-solid fa-lock" style={{ fontSize: "10px" }}></i>Internal notes are private and only visible to administrators
                </div>
              </div>
              <div className="d-flex gap-3">
                <button type="button" onClick={() => !isSubmitting && setShowNoteModal(false)} disabled={isSubmitting} style={{ flex: 1, padding: "11px", borderRadius: "8px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#64748b", fontWeight: 600, fontSize: "13px", cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.6 : 1 }}>Cancel</button>
                <button type="submit" disabled={isSubmitting || !newNote.trim()} style={{ flex: 2, padding: "11px", borderRadius: "8px", border: "none", backgroundColor: newNote.trim() ? "#294b5a" : "#94a3b8", color: "#ffffff", fontWeight: 600, fontSize: "13px", cursor: isSubmitting || !newNote.trim() ? "not-allowed" : "pointer", transition: "background-color 0.2s" }}>
                  {isSubmitting ? <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Saving...</> : <><i className="fa-solid fa-check me-1"></i>Save Note</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: "16px" }} onClick={() => !isSubmitting && setShowPaymentModal(false)}>
    <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "550px", padding: "24px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }} onClick={(e) => e.stopPropagation()}>
      <div className="d-flex justify-content-between align-items-center mb-20">
        <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 }}>
          <i className="fa-solid fa-credit-card me-2" style={{ color: "#294b5a" }}></i>Record Payment
        </h3>
        <button onClick={() => !isSubmitting && setShowPaymentModal(false)} style={{ border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: "18px" }}>
          <i className="fa-solid fa-times"></i>
        </button>
      </div>
      
      <form onSubmit={handleAddPayment}>
        <div className="row g-3 mb-15">
          <div className="col-6">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Amount *</label>
            <input 
              type="number" 
              step="0.01" 
              value={paymentAmount} 
              onChange={(e) => setPaymentAmount(e.target.value)} 
              required 
              style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc" }} 
              placeholder="0.00" 
            />
          </div>
         
  <div className="col-6">
  <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Currency</label>
  <div style={{ position: "relative" }}>
    <div 
      style={{ 
        width: "100%", 
        padding: "10px 12px", 
        borderRadius: "8px", 
        border: "1px solid #cbd5e1", 
        fontSize: "13px", 
        outline: "none", 
        backgroundColor: "#f8fafc",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "42px"
      }}
      onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
    >
      <span style={{ color: paymentCurrency ? "#1e293b" : "#94a3b8" }}>
        {paymentCurrency || "Select currency..."}
      </span>
      <i className={`fa-solid fa-chevron-${showCurrencyDropdown ? "up" : "down"}`} style={{ color: "#94a3b8", fontSize: "12px" }}></i>
    </div>
    
    {showCurrencyDropdown && (
      <div style={{ 
        position: "absolute", 
        top: "calc(100% + 4px)", 
        left: 0, 
        right: 0, 
        maxHeight: "280px", 
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff", 
        borderRadius: "8px", 
        border: "1px solid #e2e8f0", 
        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)", 
        zIndex: 9999,
        overflow: "hidden"
      }}>
        {/* Champ de recherche */}
        <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
          <input 
            type="text" 
            placeholder="Search currency..." 
            value={paymentCurrencySearch}
            onChange={(e) => setPaymentCurrencySearch(e.target.value)}
            autoFocus
            style={{ 
              width: "100%", 
              padding: "8px 12px", 
              borderRadius: "6px", 
              border: "1px solid #cbd5e1", 
              fontSize: "13px", 
              outline: "none", 
              backgroundColor: "#f8fafc",
              boxSizing: "border-box"
            }} 
          />
        </div>
        
        {/* Liste des devises */}
        <div style={{ 
          flex: 1, 
          overflowY: "auto",
          padding: "4px"
        }}>
          {filteredCurrencies.length === 0 ? (
            <div style={{ padding: "20px 12px", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
              No currency found
            </div>
          ) : (
            filteredCurrencies.map((curr) => (
              <div 
                key={curr.code}
                style={{ 
                  padding: "8px 12px", 
                  borderRadius: "6px", 
                  cursor: "pointer", 
                  display: "flex", 
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: paymentCurrency === curr.code ? "rgba(41, 75, 90, 0.08)" : "transparent",
                  fontWeight: paymentCurrency === curr.code ? 600 : 400,
                  color: paymentCurrency === curr.code ? "#294b5a" : "#334155",
                  transition: "background 0.15s ease"
                }}
                onClick={() => {
                  setPaymentCurrency(curr.code);
                  setPaymentCurrencySearch("");
                  setShowCurrencyDropdown(false);
                }}
                onMouseEnter={(e) => {
                  if (paymentCurrency !== curr.code) {
                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (paymentCurrency !== curr.code) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <span>
                  <span style={{ fontWeight: 700, marginRight: "8px", minWidth: "40px", display: "inline-block" }}>{curr.code}</span>
                  <span style={{ color: "#64748b" }}>{curr.name}</span>
                </span>
                {paymentCurrency === curr.code && (
                  <i className="fa-solid fa-check" style={{ color: "#294b5a", fontSize: "14px" }}></i>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    )}
  </div>
</div>

        </div>
        
        <div className="mb-15">
          <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Payment Method</label>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc" }}>
            <option value="cash">Cash</option>
            <option value="credit_card">Credit Card</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="online">Online</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div className="mb-15">
          <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Transaction ID</label>
          <input type="text" value={paymentTransactionId} onChange={(e) => setPaymentTransactionId(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc" }} placeholder="Optional" />
        </div>
        
        <div className="mb-15">
          <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Status</label>
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc" }}>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        
        <div className="mb-20">
          <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Description</label>
          <textarea rows={2} value={paymentDescription} onChange={(e) => setPaymentDescription(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc", resize: "vertical" }} placeholder="Payment description..." />
        </div>
        
        <div className="d-flex gap-3">
          <button type="button" onClick={() => setShowPaymentModal(false)} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#64748b", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={isSubmitting} style={{ flex: 2, padding: "10px", borderRadius: "8px", border: "none", backgroundColor: "#294b5a", color: "#ffffff", fontWeight: 600, fontSize: "13px", cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.7 : 1 }}>
            {isSubmitting ? <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Processing...</> : "Record Payment"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

    {/* Email Modal */}
{showEmailModal && (
  <div 
    style={{ 
      position: "fixed", 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: "rgba(15,23,42,0.55)", 
      backdropFilter: "blur(4px)", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      zIndex: 1050, 
      padding: "16px" 
    }} 
    onClick={() => !isSubmitting && setShowEmailModal(false)}
  >
    <div 
      style={{ 
        backgroundColor: "#ffffff", 
        borderRadius: "16px", 
        width: "100%", 
        maxWidth: "600px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }} 
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header - fixe en haut */}
      <div style={{ 
        padding: "20px 24px 16px 24px",
        borderBottom: "1px solid #f1f5f9",
        flexShrink: 0,
        backgroundColor: "#ffffff"
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 }}>
            <i className="fa-solid fa-envelope me-2" style={{ color: "#294b5a" }}></i>Send Email
          </h3>
          <button 
            onClick={() => !isSubmitting && setShowEmailModal(false)} 
            style={{ border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: "18px" }}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
        <div style={{ backgroundColor: "#f0f4f8", borderRadius: "8px", padding: "12px", marginTop: "12px" }}>
          <span style={{ fontSize: "12px", color: "#64748b" }}>To: </span>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
            {application.firstname} {application.lastname} ({application.email})
          </span>
        </div>
      </div>

      {/* Corps scrollable avec padding */}
      <div style={{ 
        padding: "16px 24px 0 24px",
        overflowY: "auto",
        flex: "1 1 auto",
        minHeight: 0
      }}>
        <form onSubmit={handleSendEmail}>
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Template</label>
            <select 
              value={emailTemplate} 
              onChange={(e) => handleEmailTemplateChange(e.target.value)} 
              style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc" }}
            >
              <option value="custom">Custom Message</option>
              <option value="document_request">Request Documents</option>
              <option value="approval">Application Approved</option>
              <option value="rejection">Application Rejected</option>
            </select>
          </div>
          
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Subject *</label>
            <input 
              type="text" 
              value={emailSubject} 
              onChange={(e) => setEmailSubject(e.target.value)} 
              required 
              style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", backgroundColor: "#f8fafc" }} 
              placeholder="Email subject..." 
            />
          </div>
          
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Message *</label>
            <textarea 
              rows={8} 
              value={emailMessage} 
              onChange={(e) => setEmailMessage(e.target.value)} 
              required 
              style={{ 
                width: "100%", 
                padding: "10px 12px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                fontSize: "13px", 
                outline: "none", 
                backgroundColor: "#f8fafc", 
                resize: "vertical", 
                fontFamily: "inherit",
                minHeight: "120px"
              }} 
              placeholder="Type your message..." 
            />
          </div>
          
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>Attachments (optional)</label>
            <input 
              type="file" 
              multiple 
              onChange={(e) => setEmailAttachments(Array.from(e.target.files || []))} 
              style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#f8fafc" }} 
            />
            {emailAttachments.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                {emailAttachments.map((file, index) => (
                  <span key={index} style={{ display: "inline-block", padding: "4px 10px", backgroundColor: "#f0f4f8", borderRadius: "4px", fontSize: "11px", color: "#64748b", marginRight: "6px", marginBottom: "4px" }}>
                    <i className="fa-solid fa-paperclip me-1" style={{ fontSize: "10px" }}></i>{file.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Espace pour que les boutons ne cachent pas le contenu */}
          <div style={{ height: "80px" }}></div>
        </form>
      </div>

      {/* Boutons - fixes en bas */}
      <div style={{ 
        display: "flex", 
        gap: "10px",
        padding: "16px 24px",
        borderTop: "1px solid #f1f5f9",
        backgroundColor: "#ffffff",
        flexShrink: 0,
        position: "sticky",
        bottom: 0
      }}>
        <button 
          type="button" 
          onClick={() => setShowEmailModal(false)} 
          style={{ 
            flex: 1, 
            padding: "10px", 
            borderRadius: "8px", 
            border: "1px solid #cbd5e1", 
            backgroundColor: "#ffffff", 
            color: "#64748b", 
            fontWeight: 600, 
            fontSize: "13px", 
            cursor: "pointer" 
          }}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={isSubmitting} 
          style={{ 
            flex: 2, 
            padding: "10px", 
            borderRadius: "8px", 
            border: "none", 
            backgroundColor: "#294b5a", 
            color: "#ffffff", 
            fontWeight: 600, 
            fontSize: "13px", 
            cursor: isSubmitting ? "not-allowed" : "pointer", 
            opacity: isSubmitting ? 0.7 : 1 
          }}
          onClick={() => {
            // Déclencher le submit du formulaire
            const form = document.querySelector('form');
            if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
          }}
        >
          {isSubmitting ? (
            <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Sending...</>
          ) : (
            <><i className="fa-solid fa-paper-plane me-1"></i>Send Email</>
          )}
        </button>
      </div>
    </div>
  </div>
)}

      {/* Visa Modal */}
{showVisaModal && (
  <div 
    style={{ 
      position: "fixed", 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: "rgba(15,23,42,0.55)", 
      backdropFilter: "blur(4px)", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      zIndex: 1050, 
      padding: "16px" 
    }} 
    onClick={() => !isSubmitting && setShowVisaModal(false)}
  >
    <div 
      style={{ 
        backgroundColor: "#ffffff", 
        borderRadius: "16px", 
        width: "100%", 
        maxWidth: "500px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }} 
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ 
        padding: "20px 24px 16px 24px",
        borderBottom: "1px solid #f1f5f9",
        flexShrink: 0,
        backgroundColor: "#ffffff"
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 }}>
            <i className="fa-solid fa-passport me-2" style={{ color: "#294b5a" }}></i>Issue Visa
          </h3>
          <button 
            onClick={() => !isSubmitting && setShowVisaModal(false)} 
            style={{ border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: "18px" }}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ 
        padding: "16px 24px 24px 24px",
        overflowY: "auto",
        flex: "1 1 auto",
        minHeight: 0
      }}>
        <form onSubmit={handleIssueVisa}>
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
              Visa Number <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input 
              type="text" 
              value={visaNumber} 
              onChange={(e) => setVisaNumber(e.target.value)} 
              required 
              style={{ 
                width: "100%", 
                padding: "10px 12px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                fontSize: "13px", 
                outline: "none", 
                backgroundColor: "#f8fafc",
                fontFamily: "monospace",
                transition: "border-color 0.2s ease"
              }}
              onFocus={(e) => e.target.style.borderColor = "#294b5a"}
              onBlur={(e) => e.target.style.borderColor = "#cbd5e1"}
              placeholder="e.g., VISA-2024-001234"
            />
          </div>
          
          <div className="row g-3 mb-20">
            <div className="col-6">
              <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
                Issue Date <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input 
                type="date" 
                value={visaIssueDate} 
                onChange={(e) => setVisaIssueDate(e.target.value)} 
                required 
                style={{ 
                  width: "100%", 
                  padding: "10px 12px", 
                  borderRadius: "8px", 
                  border: "1px solid #cbd5e1", 
                  fontSize: "13px", 
                  outline: "none", 
                  backgroundColor: "#f8fafc",
                  transition: "border-color 0.2s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#294b5a"}
                onBlur={(e) => e.target.style.borderColor = "#cbd5e1"}
              />
            </div>
            <div className="col-6">
              <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
                Expiry Date <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input 
                type="date" 
                value={visaExpiryDate} 
                onChange={(e) => setVisaExpiryDate(e.target.value)} 
                required 
                style={{ 
                  width: "100%", 
                  padding: "10px 12px", 
                  borderRadius: "8px", 
                  border: "1px solid #cbd5e1", 
                  fontSize: "13px", 
                  outline: "none", 
                  backgroundColor: "#f8fafc",
                  transition: "border-color 0.2s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#294b5a"}
                onBlur={(e) => e.target.style.borderColor = "#cbd5e1"}
              />
            </div>
          </div>

          {/* Boutons */}
          <div style={{ 
            display: "flex", 
            gap: "10px",
            paddingTop: "16px",
            borderTop: "1px solid #f1f5f9"
          }}>
            <button 
              type="button" 
              onClick={() => setShowVisaModal(false)} 
              style={{ 
                flex: 1, 
                padding: "10px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                backgroundColor: "#ffffff", 
                color: "#64748b", 
                fontWeight: 600, 
                fontSize: "13px", 
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.borderColor = "#94a3b8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting} 
              style={{ 
                flex: 2, 
                padding: "10px", 
                borderRadius: "8px", 
                border: "none", 
                backgroundColor: "#294b5a", 
                color: "#ffffff", 
                fontWeight: 600, 
                fontSize: "13px", 
                cursor: isSubmitting ? "not-allowed" : "pointer", 
                opacity: isSubmitting ? 0.7 : 1,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = "#1e3743";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = "#294b5a";
                }
              }}
            >
              {isSubmitting ? (
                <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Issuing...</>
              ) : (
                <><i className="fa-solid fa-check me-1" style={{ color: "#d3be76" }}></i>Issue Visa</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

      {/* Appointment Modal */}
{showAppointmentModal && (
  <div 
    style={{ 
      position: "fixed", 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: "rgba(15,23,42,0.55)", 
      backdropFilter: "blur(4px)", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      zIndex: 1050, 
      padding: "16px" 
    }} 
    onClick={() => !isSubmitting && setShowAppointmentModal(false)}
  >
    <div 
      style={{ 
        backgroundColor: "#ffffff", 
        borderRadius: "16px", 
        width: "100%", 
        maxWidth: "500px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }} 
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ 
        padding: "20px 24px 16px 24px",
        borderBottom: "1px solid #f1f5f9",
        flexShrink: 0,
        backgroundColor: "#ffffff"
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 }}>
            <i className="fa-solid fa-calendar-plus me-2" style={{ color: "#294b5a" }}></i>Schedule Appointment
          </h3>
          <button 
            onClick={() => !isSubmitting && setShowAppointmentModal(false)} 
            style={{ border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: "18px" }}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ 
        padding: "16px 24px 24px 24px",
        overflowY: "auto",
        flex: "1 1 auto",
        minHeight: 0
      }}>
        <form onSubmit={handleScheduleAppointment}>
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
              Date & Time <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input 
              type="datetime-local" 
              value={appointmentDate} 
              onChange={(e) => setAppointmentDate(e.target.value)} 
              required 
              style={{ 
                width: "100%", 
                padding: "10px 12px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                fontSize: "13px", 
                outline: "none", 
                backgroundColor: "#f8fafc",
                transition: "border-color 0.2s ease"
              }}
              onFocus={(e) => e.target.style.borderColor = "#294b5a"}
              onBlur={(e) => e.target.style.borderColor = "#cbd5e1"}
            />
          </div>
          
          <div className="mb-15">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
              Location <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input 
              type="text" 
              value={appointmentLocation} 
              onChange={(e) => setAppointmentLocation(e.target.value)} 
              required 
              style={{ 
                width: "100%", 
                padding: "10px 12px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                fontSize: "13px", 
                outline: "none", 
                backgroundColor: "#f8fafc",
                transition: "border-color 0.2s ease"
              }}
              onFocus={(e) => e.target.style.borderColor = "#294b5a"}
              onBlur={(e) => e.target.style.borderColor = "#cbd5e1"}
              placeholder="e.g., Main Office, Embassy, etc."
            />
          </div>
          
          <div className="mb-20">
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
              Notes <span style={{ color: "#94a3b8", fontSize: "11px", fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea 
              rows={3} 
              value={appointmentNotes} 
              onChange={(e) => setAppointmentNotes(e.target.value)} 
              style={{ 
                width: "100%", 
                padding: "10px 12px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                fontSize: "13px", 
                outline: "none", 
                backgroundColor: "#f8fafc", 
                resize: "vertical",
                fontFamily: "inherit",
                transition: "border-color 0.2s ease"
              }}
              onFocus={(e) => e.target.style.borderColor = "#294b5a"}
              onBlur={(e) => e.target.style.borderColor = "#cbd5e1"}
              placeholder="Additional instructions or notes..."
            />
          </div>

          {/* Boutons */}
          <div style={{ 
            display: "flex", 
            gap: "10px",
            paddingTop: "16px",
            borderTop: "1px solid #f1f5f9"
          }}>
            <button 
              type="button" 
              onClick={() => setShowAppointmentModal(false)} 
              style={{ 
                flex: 1, 
                padding: "10px", 
                borderRadius: "8px", 
                border: "1px solid #cbd5e1", 
                backgroundColor: "#ffffff", 
                color: "#64748b", 
                fontWeight: 600, 
                fontSize: "13px", 
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.borderColor = "#94a3b8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting} 
              style={{ 
                flex: 2, 
                padding: "10px", 
                borderRadius: "8px", 
                border: "none", 
                backgroundColor: "#294b5a", 
                color: "#ffffff", 
                fontWeight: 600, 
                fontSize: "13px", 
                cursor: isSubmitting ? "not-allowed" : "pointer", 
                opacity: isSubmitting ? 0.7 : 1,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = "#1e3743";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = "#294b5a";
                }
              }}
            >
              {isSubmitting ? (
                <><i className="fa-solid fa-circle-notch fa-spin me-1"></i>Scheduling...</>
              ) : (
                <><i className="fa-solid fa-check me-1" style={{ color: "#d3be76" }}></i>Schedule</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: "16px" }} onClick={() => setShowDeleteModal(false)}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "420px", padding: "24px", textAlign: "center", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }} onClick={(e) => e.stopPropagation()}>
            <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "36px", color: "#dc2626", marginBottom: "12px" }}></i>
            <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: "0 0 8px 0" }}>Confirm Deletion</h3>
            <p style={{ fontSize: "13.5px", color: "#64748b", margin: "0 0 20px 0" }}>
              Are you sure you want to permanently delete application <strong>{application.reference_number}</strong>? All associated data will be permanently removed.
            </p>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#64748b", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleDeleteApplication} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", backgroundColor: "#dc2626", color: "#ffffff", fontWeight: 600, cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Document Modal */}
{showDeleteDocumentModal && (
  <div 
    style={{ 
      position: "fixed", 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: "rgba(15,23,42,0.55)", 
      backdropFilter: "blur(4px)", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      zIndex: 1050, 
      padding: "16px" 
    }} 
    onClick={() => {
      setShowDeleteDocumentModal(false);
      setDocumentToDelete(null);
    }}
  >
    <div 
      style={{ 
        backgroundColor: "#ffffff", 
        borderRadius: "16px", 
        width: "100%", 
        maxWidth: "420px", 
        padding: "24px", 
        textAlign: "center", 
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" 
      }} 
      onClick={(e) => e.stopPropagation()}
    >
      <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "36px", color: "#dc2626", marginBottom: "12px" }}></i>
      <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: "0 0 8px 0" }}>Delete Document</h3>
      <p style={{ fontSize: "13.5px", color: "#64748b", margin: "0 0 20px 0" }}>
        Are you sure you want to remove this document? This action cannot be undone.
      </p>
      <div style={{ display: "flex", gap: "10px" }}>
        <button 
          onClick={() => {
            setShowDeleteDocumentModal(false);
            setDocumentToDelete(null);
          }} 
          style={{ 
            flex: 1, 
            padding: "10px", 
            borderRadius: "8px", 
            border: "1px solid #cbd5e1", 
            backgroundColor: "#ffffff", 
            color: "#64748b", 
            fontWeight: 600, 
            cursor: "pointer" 
          }}
        >
          Cancel
        </button>
        <button 
          onClick={confirmDeleteDocument} 
          style={{ 
            flex: 1, 
            padding: "10px", 
            borderRadius: "8px", 
            border: "none", 
            backgroundColor: "#dc2626", 
            color: "#ffffff", 
            fontWeight: 600, 
            cursor: "pointer" 
          }}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
)}

      
    </>
  );
};

export default VisaApplicationDetail;